import clsx from "clsx";
import { ElementType, HTMLAttributes, ReactNode } from "react";

type Variant = "primary" | "secondary";
type Size = "xs" | "sm" | "md" | "lg" | "xl";
type Weight = "regular" | "semibold" | "bold";
type Color = "green" | "pink" | "muted" | "subtle" | "light";

type TextProps = {
  variant?: Variant;
  size?: Size;
  weight?: Weight;
  color?: Color;
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & Omit<HTMLAttributes<HTMLElement>, "color">;

const SIZE_CLASSES: Record<Variant, Record<Size, string>> = {
  primary: {
    xs: "text-base",
    sm: "text-xl",
    md: "text-2xl",
    lg: "text-3xl",
    xl: "text-[2.5rem] md:text-5xl lg:text-[3.5rem]",
  },
  secondary: {
    xs: "text-xs",
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
    xl: "text-xl",
  },
};

const WEIGHT_CLASSES: Record<Weight, string> = {
  regular: "font-normal",
  semibold: "font-semibold",
  bold: "font-bold",
};

const COLOR_CLASSES: Record<Color, string> = {
  green: "text-butter-green",
  pink: "text-butter-pink",
  muted: "text-butter-green",
  subtle: "text-butter-green",
  light: "text-butter-cream-light",
};

export function Text({
  variant = "secondary",
  size = "md",
  weight,
  color = "green",
  as,
  className,
  children,
  ...rest
}: TextProps) {
  const isPrimary = variant === "primary";
  const Tag = (as ?? (isPrimary ? "h2" : "p")) as ElementType;
  const fontFamily = isPrimary ? "font-serif" : "font-sans";
  const lineHeight = isPrimary
    ? size === "xl"
      ? "leading-[1]"
      : "leading-tight"
    : "";
  const resolvedWeight: Weight = weight ?? (isPrimary ? "bold" : "regular");

  return (
    <Tag
      className={clsx(
        fontFamily,
        SIZE_CLASSES[variant][size],
        WEIGHT_CLASSES[resolvedWeight],
        COLOR_CLASSES[color],
        lineHeight,
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
